/**
 * Formatting Presets - Pre-built formatting configurations
 * 
 * Quick-apply preset configurations for common use cases
 */

export const FORMATTING_PRESETS = {
    financial_report: {
        id: 'financial_report',
        name: 'Financial Report',
        description: 'Currency formatting, 2 decimal places, thousand separators',
        icon: 'currency',
        config_data: {
            standardization: {
                dates: {
                    enabled: true,
                    output_format: 'YYYY-MM-DD',
                    timezone: 'UTC',
                    input_formats: ['MM/DD/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD']
                },
                numbers: {
                    enabled: true,
                    decimal_places: 2,
                    thousand_separator: ',',
                    decimal_separator: '.',
                    scientific_threshold: 1000000
                },
                currency: {
                    enabled: true,
                    symbol: '$',
                    position: 'before',
                    decimal_places: 2,
                    code: 'USD'
                },
                text: {
                    enabled: true,
                    capitalization: 'none',
                    trim_whitespace: true,
                    encoding: 'utf-8'
                }
            },
            cleanup: {
                enabled: true,
                remove_empty_rows: true,
                remove_empty_columns: true,
                merge_duplicate_headers: false,
                group_similar_data: false
            },
            highlighting: {
                duplicates: {
                    enabled: true,
                    color: '#ffeb3b',
                    action: 'highlight'
                },
                inconsistencies: {
                    enabled: true,
                    color: '#f44336',
                    action: 'flag'
                }
            },
            conditional_formatting: [
                {
                    condition: 'value > 10000',
                    style: {
                        background_color: '#e8f5e8',
                        text_color: '#2e7d32',
                        font_weight: 'bold'
                    },
                    description: 'Highlight high values'
                },
                {
                    condition: 'value < 0',
                    style: {
                        background_color: '#ffebee',
                        text_color: '#c62828',
                        font_weight: 'normal'
                    },
                    description: 'Highlight negative values'
                }
            ],
            reports: {
                summary_enabled: true,
                executive_summary: true,
                audit_report: true,
                custom_reports: []
            },
            export: {
                format: 'xlsx',
                include_headers: true,
                freeze_header_row: true
            }
        }
    },

    date_standardization: {
        id: 'date_standardization',
        name: 'Date Standardization',
        description: 'ISO date format, UTC timezone, auto-detect input formats',
        icon: 'date',
        config_data: {
            standardization: {
                dates: {
                    enabled: true,
                    output_format: 'YYYY-MM-DD',
                    timezone: 'UTC',
                    input_formats: ['MM/DD/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY', 'M/D/YY']
                },
                numbers: {
                    enabled: false,
                    decimal_places: 2,
                    thousand_separator: ',',
                    decimal_separator: '.',
                    scientific_threshold: 1000000
                },
                currency: {
                    enabled: false,
                    symbol: '$',
                    position: 'before',
                    decimal_places: 2,
                    code: 'USD'
                },
                text: {
                    enabled: true,
                    capitalization: 'none',
                    trim_whitespace: true,
                    encoding: 'utf-8'
                }
            },
            cleanup: {
                enabled: true,
                remove_empty_rows: true,
                remove_empty_columns: false,
                merge_duplicate_headers: false,
                group_similar_data: false
            },
            highlighting: {
                duplicates: {
                    enabled: false,
                    color: '#ffeb3b',
                    action: 'highlight'
                },
                inconsistencies: {
                    enabled: true,
                    color: '#ff9800',
                    action: 'flag'
                }
            },
            conditional_formatting: [],
            reports: {
                summary_enabled: true,
                executive_summary: false,
                audit_report: true,
                custom_reports: []
            },
            export: {
                format: 'xlsx',
                include_headers: true,
                freeze_header_row: true
            }
        }
    },

    excel_export_ready: {
        id: 'excel_export_ready',
        name: 'Excel Export Ready',
        description: 'Optimized for Excel export with frozen headers and auto-width columns',
        icon: 'reports',
        config_data: {
            standardization: {
                dates: {
                    enabled: true,
                    output_format: 'YYYY-MM-DD',
                    timezone: 'UTC',
                    input_formats: ['MM/DD/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD']
                },
                numbers: {
                    enabled: true,
                    decimal_places: 2,
                    thousand_separator: ',',
                    decimal_separator: '.',
                    scientific_threshold: 1000000
                },
                currency: {
                    enabled: true,
                    symbol: '$',
                    position: 'before',
                    decimal_places: 2,
                    code: 'USD'
                },
                text: {
                    enabled: true,
                    capitalization: 'title',
                    trim_whitespace: true,
                    encoding: 'utf-8'
                }
            },
            cleanup: {
                enabled: true,
                remove_empty_rows: true,
                remove_empty_columns: true,
                merge_duplicate_headers: true,
                group_similar_data: false
            },
            highlighting: {
                duplicates: {
                    enabled: true,
                    color: '#fff3e0',
                    action: 'highlight'
                },
                inconsistencies: {
                    enabled: true,
                    color: '#ffebee',
                    action: 'flag'
                }
            },
            conditional_formatting: [],
            reports: {
                summary_enabled: true,
                executive_summary: false,
                audit_report: false,
                custom_reports: []
            },
            sorting: {
                sortBy: [],
                groupBy: '',
                outputType: 'single_sheet_grouped',
                showSectionHeaders: true,
                showSubtotals: true,
                showGrandTotal: true,
                colorScheme: 'professional'
            },
            export: {
                format: 'xlsx',
                include_headers: true,
                freeze_header_row: true,
                auto_column_width: true,
                add_filters: true
            }
        }
    },

    data_cleanup: {
        id: 'data_cleanup',
        name: 'Data Cleanup',
        description: 'Focus on removing duplicates, empty rows, and standardizing text',
        icon: 'cleanup',
        config_data: {
            standardization: {
                dates: {
                    enabled: false,
                    output_format: 'YYYY-MM-DD',
                    timezone: 'UTC',
                    input_formats: []
                },
                numbers: {
                    enabled: false,
                    decimal_places: 2,
                    thousand_separator: ',',
                    decimal_separator: '.',
                    scientific_threshold: 1000000
                },
                currency: {
                    enabled: false,
                    symbol: '$',
                    position: 'before',
                    decimal_places: 2,
                    code: 'USD'
                },
                text: {
                    enabled: true,
                    capitalization: 'sentence',
                    trim_whitespace: true,
                    encoding: 'utf-8'
                }
            },
            cleanup: {
                enabled: true,
                remove_empty_rows: true,
                remove_empty_columns: true,
                merge_duplicate_headers: true,
                group_similar_data: true
            },
            highlighting: {
                duplicates: {
                    enabled: true,
                    color: '#ffeb3b',
                    action: 'highlight'
                },
                inconsistencies: {
                    enabled: true,
                    color: '#f44336',
                    action: 'flag'
                }
            },
            conditional_formatting: [],
            reports: {
                summary_enabled: true,
                executive_summary: false,
                audit_report: true,
                custom_reports: []
            },
            export: {
                format: 'xlsx',
                include_headers: true,
                freeze_header_row: false
            }
        }
    }
};

/**
 * Get all available presets
 */
export const getPresets = () => Object.values(FORMATTING_PRESETS);

/**
 * Get a specific preset by ID
 */
export const getPresetById = (id) => FORMATTING_PRESETS[id] || null;

/**
 * Apply a preset to existing form data (merges with existing data)
 */
export const applyPreset = (presetId, existingFormData = {}) => {
    const preset = getPresetById(presetId);
    if (!preset) return existingFormData;

    return {
        ...existingFormData,
        config_data: {
            ...preset.config_data
        }
    };
};

export default FORMATTING_PRESETS;
